import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Chip, IconButton, Grid, Alert, LinearProgress,
  Button, Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AutoAwesome as EnrichIcon,
  PersonSearch as DiscoveryIcon,
  MailOutlined as OutreachIcon,
  HealthAndSafety as DomainIcon,
  Schedule as ScheduleIcon,
  MarkEmailRead as InboxIcon,
  OpenInNew as OpenInNewIcon,
  Bolt as ActiveIcon,
  PauseCircleFilled as PausedIcon,
  CheckCircle as RunningIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  listAutoEnrichmentRuns,
  listContactDiscoveryRuns,
  listAutoOutreachRuns,
  listDomainCheckRuns,
  getEmailQueueStatusCounts,
  getImapPollerStatus,
} from '../services/api';
import AzotteGrid from '../components/AzotteGrid';

const STATUS_CHIP_COLOR = {
  Pending: 'default',
  Running: 'info',
  Cancelling: 'warning',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
};

const ACTIVE_STATUSES = new Set(['Pending', 'Running', 'Cancelling']);

const JOB_TYPES = {
  enrichment: {
    label: 'Auto Enrichment',
    icon: EnrichIcon,
    color: '#6366f1',
    detailPath: (id) => `/leads/auto-enrichment/${id}`,
    progressFields: (r) => ({ done: r.processed ?? 0, total: r.total ?? 0 }),
  },
  discovery: {
    label: 'Contact Discovery',
    icon: DiscoveryIcon,
    color: '#0ea5e9',
    detailPath: (id) => `/leads/contact-discovery/${id}`,
    progressFields: (r) => ({ done: r.processedLeads ?? 0, total: r.totalLeads ?? 0 }),
  },
  outreach: {
    label: 'Auto Outreach',
    icon: OutreachIcon,
    color: '#8b5cf6',
    detailPath: (id) => `/leads/auto-outreach/${id}`,
    progressFields: (r) => ({ done: r.processedLeads ?? 0, total: r.totalLeads ?? 0 }),
  },
  domain: {
    label: 'Domain Check',
    icon: DomainIcon,
    color: '#10b981',
    detailPath: () => `/leads/domain-check`,
    progressFields: (r) => ({ done: r.processed ?? 0, total: r.total ?? 0 }),
  },
};

function fmtSecs(s) {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function fmtDuration(start, end, accumulatedSeconds) {
  const acc = accumulatedSeconds || 0;
  if (end) {
    if (acc > 0) return fmtSecs(acc);
    if (start) {
      const s = Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000));
      return fmtSecs(s);
    }
    return '—';
  }
  if (!start) return acc > 0 ? fmtSecs(acc) : '—';
  const live = Math.max(0, Math.round((Date.now() - new Date(start).getTime()) / 1000));
  return fmtSecs(acc + live);
}

function fmtRelative(d) {
  if (!d) return '—';
  const diff = Math.round((Date.now() - new Date(d).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(d).toLocaleString();
}

function WorkerCard({ icon: Icon, color, title, subtitle, paused, enabled = true, lastTickAt, lastTickError, stats, onView }) {
  const dotColor = !enabled ? '#94a3b8' : paused ? '#f59e0b' : '#10b981';
  const dotLabel = !enabled ? 'Disabled' : paused ? 'Paused' : 'Running';
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5,
          bgcolor: `${color}15`, color, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>{title}</Typography>
            <Chip
              size="small"
              icon={paused ? <PausedIcon sx={{ fontSize: 14 }} /> : <RunningIcon sx={{ fontSize: 14 }} />}
              label={dotLabel}
              sx={{
                height: 20, fontSize: '0.7rem', fontWeight: 700,
                bgcolor: `${dotColor}20`, color: dotColor,
                '& .MuiChip-icon': { color: dotColor },
              }}
            />
          </Stack>
          {subtitle && <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{subtitle}</Typography>}
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
            Last tick: <strong>{fmtRelative(lastTickAt)}</strong>
          </Typography>
          {stats && (
            <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              {stats.map((s) => (
                <Box key={s.label}>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>{s.label}</Typography>{' '}
                  <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: s.color || '#0f172a' }}>
                    {s.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
          {lastTickError && (
            <Alert severity="error" icon={<WarningIcon fontSize="small" />} sx={{ mt: 1, py: 0, fontSize: 12 }}>
              {lastTickError}
            </Alert>
          )}
        </Box>
        {onView && (
          <Button size="small" variant="text" onClick={onView} sx={{ textTransform: 'none' }}>
            Open
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

export default function AutomationOverviewPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [queueCounts, setQueueCounts] = useState(null);
  const [imap, setImap] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const [enr, disc, out, dom, qc, imp] = await Promise.allSettled([
        listAutoEnrichmentRuns(20),
        listContactDiscoveryRuns(20),
        listAutoOutreachRuns(20),
        listDomainCheckRuns(20),
        getEmailQueueStatusCounts(),
        getImapPollerStatus(),
      ]);

      const merged = [];
      const pushAll = (typeKey, res) => {
        if (res.status !== 'fulfilled') return;
        const rows = Array.isArray(res.value?.data) ? res.value.data : (res.value?.data?.items || []);
        for (const r of rows) merged.push({ ...r, _type: typeKey });
      };
      pushAll('enrichment', enr);
      pushAll('discovery', disc);
      pushAll('outreach', out);
      pushAll('domain', dom);

      // Sort: active first, then by startedAt/createdAt desc
      merged.sort((a, b) => {
        const aActive = ACTIVE_STATUSES.has(a.status) ? 0 : 1;
        const bActive = ACTIVE_STATUSES.has(b.status) ? 0 : 1;
        if (aActive !== bActive) return aActive - bActive;
        const at = new Date(a.startedAt || a.createdAt || 0).getTime();
        const bt = new Date(b.startedAt || b.createdAt || 0).getTime();
        return bt - at;
      });

      setJobs(merged);
      setQueueCounts(qc.status === 'fulfilled' ? qc.value.data : null);
      setImap(imp.status === 'fulfilled' ? imp.value.data : null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load automation overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => { load(); setTick((x) => x + 1); }, 10000);
    return () => clearInterval(t);
  }, [load]);

  const active = useMemo(() => jobs.filter((j) => ACTIVE_STATUSES.has(j.status)), [jobs]);
  const recent = useMemo(() => jobs.filter((j) => !ACTIVE_STATUSES.has(j.status)).slice(0, 25), [jobs]);

  const renderTypeCell = (_, row) => {
    const def = JOB_TYPES[row._type];
    const Icon = def.icon;
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{
          width: 22, height: 22, borderRadius: 1, bgcolor: `${def.color}18`, color: def.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon sx={{ fontSize: 14 }} />
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{def.label}</Typography>
      </Stack>
    );
  };

  const renderProgress = (_, row) => {
    const def = JOB_TYPES[row._type];
    const { done, total } = def.progressFields(row);
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    return (
      <Box sx={{ minWidth: 120 }}>
        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>
          {done}/{total || '—'} {total > 0 ? `(${pct}%)` : ''}
        </Typography>
        {total > 0 && (
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 4, borderRadius: 2,
              bgcolor: '#e2e8f0',
              '& .MuiLinearProgress-bar': {
                bgcolor: ACTIVE_STATUSES.has(row.status) ? '#6366f1' : (row.status === 'Failed' ? '#dc2626' : '#16a34a'),
              },
            }}
          />
        )}
      </Box>
    );
  };

  const columns = [
    { key: '_type', header: 'Type', render: renderTypeCell },
    { key: 'id', header: 'ID', render: (v) => <Typography variant="body2" sx={{ fontWeight: 600 }}>#{v}</Typography> },
    {
      key: 'status', header: 'Status',
      render: (v) => <Chip size="small" label={v} color={STATUS_CHIP_COLOR[v] || 'default'} sx={{ fontWeight: 600 }} />,
    },
    { key: 'progress', header: 'Progress', render: renderProgress },
    {
      key: 'startedAt', header: 'Started', hideOnMobile: true,
      render: (v, row) => {
        const t = v || row.createdAt;
        return t ? <Tooltip title={new Date(t).toLocaleString()}><span>{fmtRelative(t)}</span></Tooltip> : '—';
      },
    },
    {
      key: 'duration', header: 'Duration', hideOnMobile: true,
      render: (_, row) => fmtDuration(row.startedAt, row.finishedAt, row.accumulatedSeconds),
    },
    {
      key: 'createdByUsername', header: 'By', hideOnMobile: true, hideOnTablet: true,
      render: (v) => v || '—',
    },
    {
      key: 'actions', header: '',
      render: (_, row) => {
        const def = JOB_TYPES[row._type];
        return (
          <Button
            size="small" variant="outlined"
            startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            onClick={() => navigate(def.detailPath(row.id))}
            sx={{ textTransform: 'none', borderRadius: 1.5 }}
          >
            View
          </Button>
        );
      },
    },
  ];

  const emailWorkerPaused = queueCounts?.workerPaused;
  const emailWorkerStats = queueCounts ? [
    { label: 'Sent', value: queueCounts.workerLastTickSent || 0, color: '#16a34a' },
    { label: 'Failed', value: queueCounts.workerLastTickFailed || 0, color: queueCounts.workerLastTickFailed > 0 ? '#dc2626' : '#94a3b8' },
    { label: 'Processed', value: queueCounts.workerLastTickProcessed || 0 },
  ] : [];

  const imapStats = imap ? [
    { label: 'Fetched', value: imap.lastTickFetched || 0 },
    { label: 'Matched', value: imap.lastTickMatched || 0, color: '#16a34a' },
  ] : [];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: { xs: 2, md: 3 } }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            <ActiveIcon color="primary" /> Automation & Jobs
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
            Background workers, running jobs, and recent runs. Auto-refresh every 10s.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {tick > 0 && (
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Updated {fmtRelative(new Date(Date.now() - 0))}
            </Typography>
          )}
          <IconButton size="small" onClick={load} title="Refresh now"><RefreshIcon fontSize="small" /></IconButton>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Background Workers */}
      <Typography variant="subtitle2" sx={{ color: '#475569', mb: 1, fontWeight: 700 }}>Background Workers</Typography>
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <WorkerCard
            icon={ScheduleIcon}
            color="#6366f1"
            title="Scheduled Email Worker"
            subtitle={queueCounts ? `Every ${queueCounts.workerIntervalSeconds}s · batch ${queueCounts.workerBatchSize} · max retries ${queueCounts.workerMaxRetries}` : 'Loading…'}
            paused={!!emailWorkerPaused}
            lastTickAt={queueCounts?.workerLastTickAt}
            stats={emailWorkerStats}
            onView={() => navigate('/email-queue/monitor')}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <WorkerCard
            icon={InboxIcon}
            color="#0ea5e9"
            title="IMAP Inbox Poller"
            subtitle={imap ? (imap.enabled ? 'Polling configured inbox' : 'Disabled in config') : 'Loading…'}
            paused={!!imap?.paused}
            enabled={imap ? !!imap.enabled : true}
            lastTickAt={imap?.lastTickAt}
            lastTickError={imap?.lastTickError}
            stats={imapStats}
            onView={() => navigate('/outreach/inbox')}
          />
        </Grid>
      </Grid>

      {/* Email Queue counts */}
      {queueCounts && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 3, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#475569', flex: 1, fontWeight: 700 }}>Email Queue</Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Sent today: <strong>{queueCounts.sentToday}/{queueCounts.dailySendLimit}</strong> · Remaining: <strong>{queueCounts.dailyRemaining}</strong>
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {[
              { label: 'Draft', value: queueCounts.draft, color: 'default' },
              { label: 'Scheduled', value: queueCounts.scheduled, color: 'info' },
              { label: 'Ready', value: queueCounts.readyToSend, color: 'primary' },
              { label: 'Needs review', value: queueCounts.needsReview, color: 'warning' },
              { label: 'Failed (retryable)', value: queueCounts.failedRetryable, color: queueCounts.failedRetryable > 0 ? 'warning' : 'default' },
              { label: 'Failed', value: queueCounts.failed, color: queueCounts.failed > 0 ? 'error' : 'default' },
              { label: 'Sent', value: queueCounts.sent, color: 'success' },
            ].map((c) => (
              <Chip
                key={c.label}
                size="small"
                label={`${c.label}: ${c.value}`}
                color={c.color}
                variant={c.value > 0 ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Stack>
          {queueCounts.nextScheduledFor && (
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
              Next scheduled: <strong>{new Date(queueCounts.nextScheduledFor).toLocaleString()}</strong>
            </Typography>
          )}
        </Paper>
      )}

      {/* Active jobs */}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#475569', flex: 1, fontWeight: 700 }}>
            Active jobs
          </Typography>
          <Chip
            size="small"
            label={active.length}
            sx={{ height: 22, fontWeight: 700, bgcolor: active.length > 0 ? '#dcfce7' : '#f1f5f9', color: active.length > 0 ? '#15803d' : '#64748b' }}
          />
        </Stack>
        {active.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#94a3b8', py: 2, textAlign: 'center' }}>
            {loading ? 'Loading…' : 'No active automation jobs right now.'}
          </Typography>
        ) : (
          <AzotteGrid
            columns={columns}
            rows={active}
            totalCount={active.length}
            showPagination={false}
            showToolbar={false}
            onDoubleClick={(r) => navigate(JOB_TYPES[r._type].detailPath(r.id))}
            actionColumnKey="actions"
          />
        )}
      </Paper>

      {/* Recent jobs */}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#475569', flex: 1, fontWeight: 700 }}>
            Recent runs
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>last {recent.length}</Typography>
        </Stack>
        {recent.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#94a3b8', py: 2, textAlign: 'center' }}>
            {loading ? 'Loading…' : 'No recent runs.'}
          </Typography>
        ) : (
          <AzotteGrid
            columns={columns}
            rows={recent}
            totalCount={recent.length}
            showPagination={false}
            showToolbar={false}
            onDoubleClick={(r) => navigate(JOB_TYPES[r._type].detailPath(r.id))}
            actionColumnKey="actions"
          />
        )}
      </Paper>
    </Box>
  );
}
